'use client';

import { useCallback, type ReactElement } from 'react';
import EvtShareBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/EvtShareBtn';
import ShareSnsBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ShareSnsBtn';
import Box from '@wconcept/wconcept-ui-renewal/components/atoms/Box';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import { requestAirbridgeTrackingLink } from '@apis/renewal/share';
import type { AirbridgeTrackingLinkResponse } from '@apis/renewal/share';
import { getPromotionShareImageUrl } from '@constants/promotionShare';
import useGa4Event from '@hooks/useGa4Event';
import useGlobalState from '@hooks/useGlobalState';
import useModalManager from '@hooks/useModalManager';
import useShareSns from '@hooks/useShareSns';
import { useToastPopupStore } from '@store/toast';

const PROMOTION_SHARE_GA4_AREA = 'promotion_share_button';

interface UsePromotionShareParams {
  title: string;
  promotionId?: number;
  imageUrl?: string;
}

function usePromotionShare({
  title,
  promotionId,
  imageUrl,
}: UsePromotionShareParams): {
  shareByKakao: () => Promise<void>;
  shareByUrl: () => Promise<void>;
  openMobileShareModal: () => Promise<void>;
  renderDesktopShareButtons: () => ReactElement;
  renderMobileFloatShareButton: () => ReactElement;
  renderMobileShareButtons: () => ReactElement;
} {
  const setToastMessage = useToastPopupStore(state => state.setToastMessage);
  const { shareKakao, onUrlClipboard } = useShareSns({
    onCopyToast: setToastMessage,
  });
  const { openShareModal } = useModalManager();
  const { sendGa4ButtonClick } = useGa4Event();
  const { appInfo, deviceInfo, isMobile } = useGlobalState();

  const getTargetUrl = useCallback((): string => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  const getShareUrl = useCallback(async (): Promise<
    AirbridgeTrackingLinkResponse | undefined
  > => {
    const targetUrl = getTargetUrl();

    try {
      const shareInfo = await requestAirbridgeTrackingLink({
        type: 'promotion',
        shareUrl: targetUrl,
        imageUrl: imageUrl || getPromotionShareImageUrl(promotionId),
        profileName: title,
      });

      return shareInfo || { trackingUrl: targetUrl };
    } catch {
      return { trackingUrl: targetUrl };
    }
  }, [getTargetUrl, imageUrl, promotionId, title]);

  const trackShare = useCallback(
    (type: string) => {
      sendGa4ButtonClick({
        area: PROMOTION_SHARE_GA4_AREA,
        issueNm: title,
        text: type,
        issueCd: promotionId?.toString(),
      });
    },
    [title, promotionId, sendGa4ButtonClick]
  );

  const shareByKakao = useCallback(async () => {
    const shareInfo = await getShareUrl();
    const targetUrl = shareInfo?.trackingUrl || getTargetUrl();
    trackShare('kakao');
    shareKakao(title, !!appInfo?.isNativeAppBoth, !isMobile, {
      targetUrl,
    });
  }, [
    appInfo?.isNativeAppBoth,
    getShareUrl,
    getTargetUrl,
    isMobile,
    shareKakao,
    title,
    trackShare,
  ]);

  const shareByUrl = useCallback(async () => {
    const shareInfo = await getShareUrl();
    const targetUrl = shareInfo?.trackingUrl || getTargetUrl();
    trackShare('url');
    onUrlClipboard({
      isMobile: !!isMobile,
      device: deviceInfo?.device || '',
      isNativeAppBoth: !!appInfo?.isNativeAppBoth,
      targetUrl,
    });
  }, [
    appInfo?.isNativeAppBoth,
    deviceInfo?.device,
    getShareUrl,
    getTargetUrl,
    isMobile,
    onUrlClipboard,
    trackShare,
  ]);

  const openMobileShareModal = useCallback(async () => {
    const shareInfo = await getShareUrl();
    const trackingUrl = shareInfo?.trackingUrl || getTargetUrl();

    openShareModal({
      title,
      targetUrl: trackingUrl,
      onClickShare: trackShare,
      shareTypes: ['kakao', 'url'],
      manageNativeActionBar: true,
      onCopyToast: setToastMessage,
    });
  }, [
    getShareUrl,
    getTargetUrl,
    openShareModal,
    setToastMessage,
    title,
    trackShare,
  ]);

  const renderDesktopShareButtons = useCallback(
    (): ReactElement => (
      <div className="share-btn-wrap">
        <ShareSnsBtn type="kakao-yellow" onClick={shareByKakao} />
        <ShareSnsBtn type="url-gray" onClick={shareByUrl} />
      </div>
    ),
    [shareByKakao, shareByUrl]
  );

  const renderMobileShareButtons = useCallback(
    (): ReactElement => (
      <Box className="corner-mobile-share">
        <Text>공유하기</Text>
        <div className="btn-wrap">
          <ShareSnsBtn
            type="kakao"
            onClick={() => {
              shareByKakao();
            }}
          />
          <ShareSnsBtn
            type="url"
            onClick={() => {
              shareByUrl();
            }}
          />
        </div>
      </Box>
    ),
    [shareByKakao, shareByUrl]
  );

  const renderMobileFloatShareButton = useCallback(
    (): ReactElement => (
      <Box as="section" className="share-btn-wrap">
        <EvtShareBtn type="share" onClick={openMobileShareModal} />
      </Box>
    ),
    [openMobileShareModal]
  );

  return {
    shareByKakao,
    shareByUrl,
    openMobileShareModal,
    renderDesktopShareButtons,
    renderMobileFloatShareButton,
    renderMobileShareButtons,
  };
}

export default usePromotionShare;
